/**
 * Shared rendering primitives for the budget dashboard (Phase 6).
 *
 * Two invariants live here so no individual view can get them wrong:
 *
 *  1. **Money is always rendered through `useMoney`**, which labels every
 *     amount with the deployment's BILLING currency. Nothing here converts:
 *     the figures arriving from the API are already denominated in the
 *     currency the tenant is charged in, so a rate applied at render time
 *     would be a second conversion. There is no approximation and no `≈`.
 *  2. **`null` cap ≠ `0` cap.** `null` is unlimited (metered, not
 *     ceilinged); `0` is a hard block. `CapText`/`CapInput` render them as
 *     visually distinct states, never as the same "empty" cell.
 */
import { useMemo, type ReactNode } from 'react'
import { useTranslation } from '@papan/i18n'
import {
  formatMoney,
  type BudgetCap,
} from '@papan/types'
import { useBillingCurrency, Tooltip, CARD, SECTION_LABEL } from '@papan/core-ui/ui'
import type { FeatureSpend } from '../../client/budget'

// ─── Money ──────────────────────────────────────────────────────────────

export interface Money {
  /** Format a BILLING-currency micro amount for display. */
  fmt: (microdollars: number) => string
  /**
   * Format an accrued-spend figure.
   *
   * ── This no longer converts, and that is the point ───────────────────
   *
   * It used to render `figure.displayMinor` — a per-row conversion the server
   * did from the USD `cost` column into an admin-chosen display currency, with
   * an `≈` marker because no single rate is exact across a period.
   *
   * Since the billing-currency-native flip, `figure.usd` IS the charged amount
   * in the deployment's own currency: `sumBucketUsage` totals
   * `cost_billing_micro`, which every cap is measured against. So the two
   * halves of this figure are no longer two views of one number — they are
   * conversions of DIFFERENT columns, and rendering `displayMinor` would show
   * a display-currency conversion of the USD cost beside a caption describing
   * the charged amount.
   *
   * `displayMinor` is therefore ignored here. The display layer is parked, not
   * deleted (plan section 7), so the field stays on the wire and this comment
   * stays with it — the next person to see an unread field must find the reason
   * before "fixing" it.
   */
  fmtFigure: (figure: { usd: number; displayMinor: number | null }) => string
  /** Format a cap, preserving the unlimited/blocked distinction. */
  cap: (cap: BudgetCap) => string
  /**
   * Always false now. Kept so the call sites that branch on it compile and
   * quietly stop showing a disclosure that no longer applies: the figure is
   * not an approximation of anything, it is the charged amount.
   */
  approximate: boolean
  /** Undefined now, for the same reason as `approximate`. */
  note: string | undefined
  /** The currency figures are rendered in — the deployment's billing currency. */
  currency: string
}

/**
 * The money formatter every budget surface renders through.
 *
 * ── Why it takes no argument any more ────────────────────────────────────
 *
 * It used to take the endpoint's `CurrencyConfig` and format with
 * `{ currency: displayCurrency, rate: displayCurrencyRate }`. Both halves are
 * now wrong:
 *
 *  - the RATE is a second conversion. Amounts reaching here are already in the
 *    billing currency, so multiplying by a display rate renders a number
 *    nobody is charged — plausible, unlabelled, and wrong by the whole credit
 *    rate.
 *  - the CURRENCY is the display one, which is parked and may be unset; the
 *    symbol that belongs on a charged amount is the one it was charged in.
 *
 * The argument was left in place at first and simply ignored. That is the same
 * shape as every defect this migration has produced — a value that looks
 * consulted and is not — so it is gone instead, and the three call sites say
 * what they mean.
 */
export function useMoney(): Money {
  const { t } = useTranslation('core-ui')
  const currency = useBillingCurrency()
  return useMemo(() => {
    const opts = { currency }
    // `formatMoney`, NOT `formatMoneyLabeled`: these figures are billing-native
    // (already in the deployment's own currency, nothing converted), so the
    // `≈` marker `formatMoneyLabeled` prefixes for any non-USD currency was
    // both wrong here and something the money surface deliberately drops
    // (2026-08-19). `approximate`/`note` below stay false/undefined to match.
    const fmt = (micro: number) => formatMoney(micro, opts)
    return {
      fmt,
      // Deliberately ignores `displayMinor` — see the interface.
      fmtFigure: (figure: { usd: number; displayMinor: number | null }) => fmt(figure.usd),
      cap: (c: BudgetCap) => (c === null ? t('settings.budget.unlimited') : fmt(c)),
      approximate: false,
      note: undefined,
      currency,
    }
  }, [currency, t])
}

/**
 * The minimum a renderer needs to print money. Tier-1 formats per ROW
 * (each tenant may be configured with a different currency) and so builds
 * this pair directly instead of calling `useMoney` per row.
 */
export type MoneyFmt = Pick<Money, 'fmt' | 'note'>

/** A figure plus its formatter — what every accrued-spend cell needs. */
export type FigureFmt = Pick<Money, 'fmtFigure' | 'note'>

/**
 * A money amount. Carries the approximate-currency disclosure as a title
 * whenever the figure was converted, so no converted number ever appears
 * unlabelled.
 */
export function MoneyText({ micro, money, className }: { micro: number; money: MoneyFmt; className?: string }) {
  return <span className={`tabular-nums ${className ?? ''}`} title={money.note}>{money.fmt(micro)}</span>
}

/**
 * An ACCRUED-SPEND amount the server already converted, per row, at the rates
 * that were in force (0172).
 *
 * The sibling of `MoneyText`, and the two are not interchangeable: this one
 * takes a `{ usd, displayMinor }` pair and renders the integer the server
 * computed, where `MoneyText` takes microdollars and multiplies by a single
 * rate. Point-in-time values — caps, ceilings, balances, thresholds — keep
 * `MoneyText`, because they apply going forward and genuinely do convert at one
 * rate.
 */
export function FigureText(
  { figure, money, className }:
  { figure: { usd: number; displayMinor: number | null }; money: FigureFmt; className?: string },
) {
  return <span className={`tabular-nums ${className ?? ''}`} title={money.note}>{money.fmtFigure(figure)}</span>
}

/**
 * A spend-vs-ceiling gauge. The caller gates on a real ceiling (`cap > 0`)
 * — an unlimited or blocked budget has no meaningful fill fraction, so
 * this component never receives one. Colour shifts as the ceiling nears,
 * but the figures on the right always state the same thing in text.
 */
export function UsageMeter({
  label,
  usedMicro,
  capMicro,
  money,
}: {
  label: string
  usedMicro: number
  capMicro: number
  money: Money
}) {
  const pct = Math.min(100, Math.max(0, (usedMicro / capMicro) * 100))
  const tone = pct >= 100 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-blue-500'
  return (
    <div className="themed-bg-secondary rounded-lg px-4 py-3">
      <div className="flex items-baseline justify-between gap-3 text-xs">
        <span className="themed-text-muted">{label}</span>
        <span className="themed-text tabular-nums" title={money.note}>
          {money.fmt(usedMicro)} <span className="themed-text-muted">/ {money.fmt(capMicro)}</span>
        </span>
      </div>
      <div className="mt-1.5 h-1.5 w-full rounded-full themed-bg-tertiary overflow-hidden">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

/**
 * A cap value. `null` and `0` are deliberately different-looking: an
 * unlimited cap reads as an infinity chip, a zero cap as a red "blocked"
 * chip. Rendering both as a dash (or as "0") is the bug this prevents.
 */
export function CapText({
  cap,
  money,
  /** Label for `null` when it does not mean "unlimited" (e.g. a team
   *  contribution of nothing, or a fallback-to-global-default knob). */
  nullLabel,
  /** Team caps are additive CONTRIBUTIONS: 0 means "adds nothing", which
   *  is not the hard block that a 0 *ceiling* is. */
  zeroIsBlock = true,
}: {
  cap: BudgetCap
  money: Money
  nullLabel?: string
  zeroIsBlock?: boolean
}) {
  const { t } = useTranslation('core-ui')
  if (cap === null) {
    return (
      <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">
        {nullLabel ?? t('settings.budget.unlimited')}
      </span>
    )
  }
  if (cap === 0 && zeroIsBlock) {
    return (
      <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-300" title={t('settings.budget.blockedHint')}>
        {t('settings.budget.blocked')}
      </span>
    )
  }
  return <MoneyText micro={cap} money={money} className="themed-text" />
}

// ─── Storage ────────────────────────────────────────────────────────────

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const i = Math.min(UNITS.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)))
  const v = bytes / 1024 ** i
  return `${v.toFixed(i === 0 ? 0 : v < 10 ? 2 : 1)} ${UNITS[i]}`
}

export const GB = 1024 ** 3

/**
 * A stored timestamp as a short date INCLUDING THE YEAR, or `null` when
 * there is no date to show.
 *
 * ⚠️ THE YEAR IS NOT OPTIONAL HERE, and that is what separates this from
 * `ChatStorageSection`'s own `formatDay` ("16 Oct"), which drops it
 * because a booked cleanup is always days away. These dates are uploads
 * and last-used times that are routinely a year or two old, and "16 Oct"
 * on a file from 2024 is worse than no date.
 *
 * ⚠️ `null` FOR 0, and that is the whole reason this is a helper. A
 * chat-storage row whose conversation could not be resolved carries
 * `updatedAt: 0`, and a file row from an API that predates its upload
 * column carries none — both would render as 1 January 1970, which reads
 * as a real date somebody might act on. `NaN` from an unparseable value
 * goes the same way.
 *
 * The LOCALE's own short form, so it stays narrow in every language the
 * product ships (zh-TW "2026/4/30", en-US "4/30/2026"); a long form is
 * nine characters too wide for a list whose only flexible column is the
 * file's name.
 */
export function formatDateWithYear(ms: number | null | undefined): string | null {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return null
  return new Date(ms).toLocaleDateString()
}

/** The same instant with its time, for a `title`. `null` on the same
 *  inputs as {@link formatDateWithYear}. */
export function formatTimestamp(ms: number | null | undefined): string | null {
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms <= 0) return null
  return new Date(ms).toLocaleString()
}

// `StorageBar` lived here until the money page stopped rendering a storage
// bar: Settings → Storage owns utilization, so a second, smaller copy of it on
// the money page was the same figure twice. It had no other consumer, so it
// went rather than sit exported and unrendered with a green regression suite
// implying something still used it. (Nothing replaces it: storage is a QUOTA,
// not a metered cost, so a money page has nothing to say about it — 0171.)
//
// The guarantee its docblock carried is NOT lost — the counted drive figure
// renders PLAIN, with no `measured`/≈/"Approximate" states, because the
// du/Redis measurement those described is gone (Tier-1 alignment,
// 2026-08-05_STORAGE_FOLLOWUPS.md §1.1). `formatBytes` + `notMeasured` still
// express it in CustomerDetail and CustomersList, and the Storage tab has its
// own coverage in `ui/storage/__tests__/StorageUsagePage.test.tsx`.

// `CUSTOMER_HIDDEN_FEATURES` / `customerFacingFeatures` lived here. They
// withheld a metered-but-unpriced `storage` row from customer-facing spend
// views, so a "Storage · 12.4 GB-months · Metered, not charged" line could not
// promise something about a bill nobody had priced.
//
// Migration 0171 answered that question the other way: storage is a QUOTA,
// enforced against `tenants.storage_quota_bytes`. It writes no ledger row, has
// no price, and `FEATURE_ORDER` no longer lists it — so the filter had no
// subject left and could never match. It went rather than sit exported with a
// green suite implying it still did something.
//
// If a future surface is metered before it is priced, the guarantee worth
// restoring is the CONDITIONAL one: withhold only while `cost === 0`, because
// the headline above the list counts every chargeable microdollar and an
// unconditional filter would produce a breakdown that fails to add up to it.

/**
 * The "spend by feature" pie, shared by the org dashboard and the member's own
 * Spending view so the two cannot drift into charting the same thing
 * differently.
 *
 * COST-ONLY, deliberately. A feature metered at `chargeable: false`
 * contributes 0 and would be an invisible slice, so the chart is skipped
 * entirely when nothing is chargeable — the `FeatureBreakdown` list below it
 * still shows every feature's utilization with its "Metered, not charged"
 * chip. That asymmetry between the list and the pie is the design: the list
 * answers "what did I use", the pie answers "where did the money go".
 *
 * Returns `null` when there is nothing to draw, which the callers use to skip
 * the whole Charts section rather than render an empty frame.
 */
export function useFeatureChart(
  rows: FeatureSpend[] | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
) {
  return useMemo(() => {
    // No rate. `f.cost` is already billing-currency micro, so the only
    // arithmetic left is micro -> major. Multiplying by a display rate here
    // charted a number nobody is charged, which on a pie is invisible: every
    // slice scales together, so the SHAPE stays right and only the axis lies.
    const points = (rows ?? []).filter(f => f.cost > 0).map(f => ({
      name: t(`settings.budget.feature.name.${f.feature}`, { defaultValue: f.feature }),
      spend: +(f.cost / 1_000_000).toFixed(4),
    }))
    if (!points.length) return null
    return {
      chartType: 'pie' as const,
      data: points,
      categoryKey: 'name',
      height: 240,
      series: [{ name: t('settings.budget.col.spend'), dataKey: 'spend' }],
    }
  }, [rows, t])
}

// ─── Per-feature spend ──────────────────────────────────────────────────

/** Stable presentation order, so the list doesn't reshuffle as spend moves.
 *  No `storage`: it is a quota, not a metered spend line (0171). Drive
 *  utilisation lives on Settings → Storage, against the quota it is actually
 *  checked against. */
const FEATURE_ORDER = ['llm', 'sandbox_compute', 'web_search']

function featureRank(feature: string) {
  const i = FEATURE_ORDER.indexOf(feature)
  return i === -1 ? FEATURE_ORDER.length : i
}

/**
 * Spend per metered surface.
 *
 * A feature with `chargeable = false` reports cost 0 with a real quantity
 * — metered but free. That row still renders, with its utilization and a
 * "not charged" chip: dropping zero-cost rows (or scaling the bars on cost
 * alone) would hide exactly the surfaces the platform is about to start
 * pricing. Bars therefore fall back to quantity share when no row in the
 * set costs anything.
 */
export function FeatureBreakdown({
  rows,
  money,
  /** Tight variant for a table cell — no bars. */
  compact = false,
}: {
  rows: FeatureSpend[]
  money: MoneyFmt
  compact?: boolean
}) {
  const { t } = useTranslation('core-ui')
  const shown = useMemo(
    // `?? []` guards a browser holding this bundle against an API that
    // predates the field — the alternative is a crash, not a missing panel.
    () => (rows ?? [])
      .filter(r => r.cost > 0 || r.quantity > 0)
      .sort((a, b) => featureRank(a.feature) - featureRank(b.feature)),
    [rows],
  )
  if (!shown.length) return <span className="themed-text-muted text-xs">{t('settings.budget.noData')}</span>

  const maxCost = Math.max(...shown.map(r => r.cost))
  const maxQty = Math.max(...shown.map(r => r.quantity))
  const share = (r: FeatureSpend) =>
    maxCost > 0 ? (r.cost / maxCost) * 100 : maxQty > 0 ? (r.quantity / maxQty) * 100 : 0

  return (
    <div className={compact ? 'space-y-1' : 'space-y-2'}>
      {shown.map(r => {
        const label = t(`settings.budget.feature.name.${r.feature}`, { defaultValue: r.feature })
        const qty = r.quantity > 0
          ? t(`settings.budget.feature.qty.${r.feature}`, {
              qty: r.quantity >= 10 ? Math.round(r.quantity) : Number(r.quantity.toFixed(2)),
              defaultValue: '',
            })
          : ''
        const free = r.cost === 0 && r.quantity > 0
        return (
          <div key={r.feature}>
            <div className={`flex items-baseline gap-2 ${compact ? 'text-[11px]' : 'text-sm'}`}>
              {/* Compact sits under a headline figure — the labels recede so
                  the money column carries the weight. */}
              <span className={`${compact ? 'themed-text-secondary' : 'themed-text'} flex-1 truncate`}>{label}</span>
              {qty && <span className="themed-text-muted text-[11px]">{qty}</span>}
              {free && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300"
                  title={t('settings.budget.feature.notCharged')}
                >
                  {t('settings.budget.feature.notCharged')}
                </span>
              )}
              <MoneyText micro={r.cost} money={money} className="themed-text tabular-nums" />
            </div>
            {!compact && (
              <div className="mt-1 h-1.5 w-full rounded-full themed-bg-tertiary overflow-hidden">
                <div
                  className={`h-full rounded-full ${free ? 'bg-blue-500/40' : 'bg-blue-500'}`}
                  style={{ width: `${share(r)}%` }}
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Layout ─────────────────────────────────────────────────────────────

/**
 * An info icon that reveals its helper text on hover (or tap on touch).
 *
 * The money surface moved every explanation off the page and behind one of
 * these (a product decision, 2026-08-19): the labels stay scannable and the
 * glossary — "included allowance", "pooled ceiling", "grace" — is one hover
 * away rather than competing with the figures for space.
 *
 * Inline SVG, NOT `lucide-react`: `@papan/orgs` deliberately carries no lucide
 * dependency (see the other budget panels), so the icon is drawn here. Renders
 * nothing when there is no text, so a call site can pass an optional hint
 * straight through.
 */
export function InfoHint({ text }: { text?: string }) {
  if (!text) return null
  return (
    <Tooltip content={text} always placement="top" className="inline-flex align-middle shrink-0">
      <span
        role="img"
        // The helper text itself IS the accessible name, so a screen reader
        // hears the definition — the sighted user gets it on hover, and the two
        // must not diverge. A generic "More information" label would have made
        // the glossary (pooled ceiling, included allowance, …) reachable to
        // pointers only. `tabIndex={0}` so a keyboard-only user can land on it.
        aria-label={text}
        tabIndex={0}
        className="inline-flex items-center justify-center themed-text-muted hover:themed-text focus:themed-text cursor-help ml-1 outline-none"
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
      </span>
    </Tooltip>
  )
}

/**
 * A page band in the console's card vocabulary — a `SECTION_LABEL` eyebrow with
 * an optional `InfoHint`, over its content.
 *
 * The Spending views compose from this so they read as the same product as the
 * Billing page (same `CARD` surface, same eyebrow), instead of the flat
 * `rounded-lg` fills they used to carry. `Section` (below) is left untouched for
 * the ~11 platform-console panels that still render a visible `desc` paragraph —
 * this is additive, not a global reskin.
 *
 * `card` wraps the body in a `CARD` surface (for tables and lists); omit it for
 * a group of `StatTile`s, which already carry their own surface and would
 * otherwise double up.
 */
export function Band({
  title, info, card = false, right, className, children,
}: {
  title: string
  /** Helper text, revealed behind the eyebrow's info icon. */
  info?: string
  /** Wrap the body in a CARD surface. Off for tile grids (tiles self-surface). */
  card?: boolean
  /** Optional control aligned to the eyebrow's right (a link, a count). */
  right?: ReactNode
  className?: string
  children: ReactNode
}) {
  return (
    <section className={`mb-5 ${className ?? ''}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center min-w-0">
          {/* A real heading, not a styled span: `Band` replaced `Section`
              (which was an <h3>) across Spending, and a section title that stops
              being a heading breaks the page's document outline for assistive
              tech — and the e2e that asserts `getByRole('heading', …)` on these
              section titles. `role`+`aria-level` keep the SECTION_LABEL styling
              and layout unchanged. */}
          <span className={SECTION_LABEL} role="heading" aria-level={3}>{title}</span>
          <InfoHint text={info} />
        </div>
        {right}
      </div>
      {card ? <div className={`${CARD} p-4`}>{children}</div> : children}
    </section>
  )
}

export function Section({ title, desc, info, children }: { title: string; desc?: ReactNode; info?: string; children: ReactNode }) {
  return (
    <div className="mb-6">
      {/* `info` puts the helper text behind an icon beside the title (the
          `Band` pattern); `desc` still renders a visible paragraph. A call
          site passes one or the other — never both. `InfoHint` is null when
          `info` is empty, so title-only sections are unchanged. */}
      <h3 className="themed-text text-sm font-medium mb-1 flex items-center">
        {title}
        <InfoHint text={info} />
      </h3>
      {/* The page is no longer capped (the shell was widened for the tables),
          so the measure moves here — body text stays readable while a grid
          beside it uses the full column. */}
      {desc && <p className="themed-text-muted text-xs mb-2 max-w-prose">{desc}</p>}
      {children}
    </div>
  )
}

/**
 * A figure with its name, its DEFINITION behind an info icon, and — where there
 * is one — a way to reach the control that sets it.
 *
 * Two halves, both load-bearing:
 *
 *  - The hint is an `InfoHint` beside the label (2026-08-19). It used to render
 *    as a visible clamped line under the value; the money surface moved every
 *    explanation behind a hover so the labels stay scannable and the tiles line
 *    up in a grid without ragged two-line hints. `InfoHint` carries the text as
 *    its accessible name, so no wrapper `title` is needed (and a second native
 *    tooltip would only stack on the same hover).
 *  - `onClick` makes the tile a link to wherever the figure is SET. A tile
 *    that states a configured value and cannot reach its control is how the
 *    grace ceiling ended up carrying a tooltip that named an environment
 *    variable instead.
 */
export function StatTile({
  label, value, hint, onClick,
}: {
  label: string
  value: ReactNode
  hint?: string
  onClick?: () => void
}) {
  const inner = (
    <>
      <div className="flex items-center themed-text-muted text-[11px] uppercase tracking-wide">
        <span className="truncate">{label}</span>
        <InfoHint text={hint} />
      </div>
      <div className="themed-text text-lg font-medium mt-0.5 tabular-nums">{value}</div>
    </>
  )
  // No wrapper `title`: the hint lives solely in the InfoHint above (hover
  // tooltip + accessible name), so hovering the icon can't stack a second
  // native tooltip carrying the same text.
  if (!onClick) {
    return (
      <div className="themed-bg-secondary rounded-lg px-3 py-2.5 min-w-[140px]">
        {inner}
      </div>
    )
  }
  return (
    <button
      onClick={onClick}
      className="themed-bg-secondary rounded-lg px-3 py-2.5 min-w-[140px] text-left transition-colors hover:themed-bg-tertiary w-full"
    >
      {inner}
    </button>
  )
}

/**
 * The C3 copy obligation. `warn_only` softens ONLY org-internal caps
 * (role/team/member); the platform-set organization ceiling still hard-stops
 * with a 402. Without saying so, "warn only" reads as "nothing stops me".
 */
export function EnforcementNote({ mode }: { mode: 'enforce' | 'warn_only' }) {
  const { t } = useTranslation('core-ui')
  if (mode === 'enforce') {
    return <p className="themed-text-muted text-xs">{t('settings.budget.enforce.desc')}</p>
  }
  return (
    <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2">
      <div className="text-amber-300 text-xs font-medium">{t('settings.budget.warnOnly.title')}</div>
      <p className="themed-text-secondary text-xs mt-0.5">{t('settings.budget.warnOnly.desc')}</p>
      <p className="text-amber-300/90 text-xs mt-1 font-medium">{t('settings.budget.warnOnly.ceiling')}</p>
    </div>
  )
}

export function ApproximateFootnote({ money }: { money: Money }) {
  if (!money.approximate) return null
  return (
    <p className="themed-text-muted text-[11px] mt-3">
      ≈ — {money.note}
    </p>
  )
}

/**
 * "You may look, not touch" — shown on a platform console page when the caller
 * holds `read` but not `write` on that area (phase 0, decision D2).
 *
 * Stated in words, on the page, rather than by hiding it or redirecting away.
 * The URL stays truthful and the reader learns WHY, which is the same stance
 * `OrgBudgetDashboard` and `BudgetsEditor` already take for a viewer who opens
 * an org budget URL (CLAUDE.md §Money screens). Silently rendering dead
 * controls would be the worst of the three.
 *
 * Not access control — the server refuses the write regardless. This only
 * explains a refusal the reader would otherwise meet as a 403 toast.
 */
export function ReadOnlyNotice({ note }: { note?: string }) {
  const { t } = useTranslation('core-ui')
  return (
    <div className="rounded-lg border border-blue-500/40 bg-blue-500/10 px-3 py-2 mb-4">
      <div className="text-blue-300 text-xs font-medium">
        {t('settings.platform.readOnly.title', { defaultValue: 'Read-only' })}
      </div>
      <p className="themed-text-secondary text-xs mt-0.5">
        {note ?? t('settings.platform.readOnly.desc', {
          defaultValue:
            'Your role in the platform organization lets you view this, but not change it.',
        })}
      </p>
    </div>
  )
}

